import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { MessageSquare, Send, Trash2, Edit2, Reply, CornerDownRight } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CommentForm = ({ onSubmit, placeholder, initialValue = '', onCancel, isReply = false }) => {
  const [content, setContent] = useState(initialValue);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!content.trim()) return;
    
    setLoading(true);
    try {
      await onSubmit(content.trim());
      setContent('');
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-3">
      <textarea
        value={content}
        onChange={(e) => setContent(e.target.value)}
        placeholder={placeholder}
        maxLength={2000}
        className={`w-full p-4 bg-[#0B0F0E] border border-[#52ea88]/10 text-[#E6FFF6] placeholder-[#9FBFB4]/50 resize-none focus:border-[#52ea88]/30 focus:outline-none transition-colors ${
          isReply ? 'h-20' : 'h-28'
        }`}
      />
      <div className="flex items-center justify-between">
        <span className="text-xs text-[#9FBFB4]/50">
          {content.length}/2000
        </span>
        <div className="flex gap-2">
          {onCancel && (
            <button
              type="button"
              onClick={onCancel}
              className="px-4 py-2 text-[#9FBFB4] hover:text-[#E6FFF6] text-sm transition-colors"
            >
              Cancel
            </button>
          )}
          <button
            type="submit"
            disabled={!content.trim() || loading}
            className="flex items-center gap-2 px-4 py-2 bg-[#52ea88] text-[#0B0F0E] font-medium text-sm hover:bg-[#52ea88]/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? (
              <div className="w-4 h-4 border-2 border-[#0B0F0E] border-t-transparent rounded-full animate-spin" />
            ) : (
              <Send className="w-4 h-4" />
            )}
            {isReply ? 'Reply' : 'Post'}
          </button>
        </div>
      </div>
    </form>
  );
};

const formatDate = (dateStr) => {
  const date = new Date(dateStr);
  const now = new Date();
  const diff = now - date;
  
  if (diff < 60000) return 'Just now';
  if (diff < 3600000) return `${Math.floor(diff / 60000)}m ago`;
  if (diff < 86400000) return `${Math.floor(diff / 3600000)}h ago`;
  if (diff < 604800000) return `${Math.floor(diff / 86400000)}d ago`;
  
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined
  });
};

const ReplyItem = ({ reply, onEdit, onDelete }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const handleDelete = async () => {
    if (!window.confirm('Delete this reply?')) return;
    setDeleting(true);
    await onDelete(reply.id);
    setDeleting(false);
  };

  const handleEdit = async (content) => {
    await onEdit(reply.id, content);
    setIsEditing(false);
  };

  return (
    <div className="ml-6 pl-4 border-l border-[#52ea88]/10 flex gap-3 py-3">
      {reply.user?.picture ? (
        <img src={reply.user.picture} alt={reply.user.name} className="w-6 h-6 rounded-full flex-shrink-0" />
      ) : (
        <div className="w-6 h-6 rounded-full bg-[#52ea88]/20 flex items-center justify-center flex-shrink-0">
          <span className="text-[#52ea88] text-xs">{reply.user?.name?.[0] || '?'}</span>
        </div>
      )}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-1">
          <span className="text-xs font-medium text-[#E6FFF6]">{reply.user?.name || 'Unknown'}</span>
          <span className="text-xs text-[#9FBFB4]/50">{formatDate(reply.created_at)}</span>
        </div>
        {isEditing ? (
          <CommentForm initialValue={reply.content} placeholder="Edit..." onSubmit={handleEdit} onCancel={() => setIsEditing(false)} isReply />
        ) : (
          <p className="text-[#9FBFB4] text-sm">{reply.content}</p>
        )}
        {reply.is_owner && !isEditing && (
          <div className="flex gap-3 mt-1">
            <button onClick={() => setIsEditing(true)} className="text-xs text-[#9FBFB4]/70 hover:text-[#52ea88]">
              <Edit2 className="w-3 h-3 inline mr-1" />Edit
            </button>
            <button onClick={handleDelete} disabled={deleting} className="text-xs text-[#9FBFB4]/70 hover:text-red-400">
              <Trash2 className="w-3 h-3 inline mr-1" />Delete
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

const CommentItem = ({ comment, onReply, onEdit, onDelete }) => {
  const [showReplyForm, setShowReplyForm] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const handleDelete = async () => {
    if (!window.confirm('Delete this comment?')) return;
    setDeleting(true);
    await onDelete(comment.id);
    setDeleting(false);
  };

  const handleReply = async (content) => {
    await onReply(comment.id, content);
    setShowReplyForm(false);
  };

  const handleEdit = async (content) => {
    await onEdit(comment.id, content);
    setIsEditing(false);
  };

  return (
    <div className="py-4">
      <div className="flex gap-3">
        {comment.user?.picture ? (
          <img src={comment.user.picture} alt={comment.user.name} className="w-8 h-8 rounded-full flex-shrink-0" />
        ) : (
          <div className="w-8 h-8 rounded-full bg-[#52ea88]/20 flex items-center justify-center flex-shrink-0">
            <span className="text-[#52ea88] text-sm font-medium">{comment.user?.name?.[0] || '?'}</span>
          </div>
        )}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <span className="text-sm font-medium text-[#E6FFF6]">{comment.user?.name || 'Unknown User'}</span>
            <span className="text-xs text-[#9FBFB4]/50">{formatDate(comment.created_at)}</span>
            {comment.created_at !== comment.updated_at && <span className="text-xs text-[#9FBFB4]/30">(edited)</span>}
          </div>
          {isEditing ? (
            <CommentForm initialValue={comment.content} placeholder="Edit..." onSubmit={handleEdit} onCancel={() => setIsEditing(false)} isReply />
          ) : (
            <p className="text-[#9FBFB4] text-sm whitespace-pre-wrap break-words">{comment.content}</p>
          )}
          {!isEditing && (
            <div className="flex items-center gap-4 mt-2">
              <button onClick={() => setShowReplyForm(!showReplyForm)} className="flex items-center gap-1 text-xs text-[#9FBFB4]/70 hover:text-[#52ea88]">
                <Reply className="w-3 h-3" />Reply
              </button>
              {comment.is_owner && (
                <>
                  <button onClick={() => setIsEditing(true)} className="flex items-center gap-1 text-xs text-[#9FBFB4]/70 hover:text-[#52ea88]">
                    <Edit2 className="w-3 h-3" />Edit
                  </button>
                  <button onClick={handleDelete} disabled={deleting} className="flex items-center gap-1 text-xs text-[#9FBFB4]/70 hover:text-red-400 disabled:opacity-50">
                    {deleting ? <div className="w-3 h-3 border border-current border-t-transparent rounded-full animate-spin" /> : <Trash2 className="w-3 h-3" />}Delete
                  </button>
                </>
              )}
            </div>
          )}
        </div>
      </div>
      {showReplyForm && (
        <div className="ml-11 mt-3">
          <div className="flex items-center gap-2 mb-2 text-xs text-[#9FBFB4]/50">
            <CornerDownRight className="w-3 h-3" />Replying to {comment.user?.name}
          </div>
          <CommentForm placeholder={`Reply to ${comment.user?.name}...`} onSubmit={handleReply} onCancel={() => setShowReplyForm(false)} isReply />
        </div>
      )}
      {comment.replies?.map((reply) => (
        <ReplyItem key={reply.id} reply={reply} onEdit={onEdit} onDelete={onDelete} />
      ))}
    </div>
  );
};

const CommentsSection = ({ reviewId }) => {
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [comments, setComments] = useState([]);
  const [totalComments, setTotalComments] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchComments = useCallback(async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_URL}/api/comments/review/${reviewId}`, { credentials: 'include' });
      if (response.ok) {
        const data = await response.json();
        setComments(data.comments);
        setTotalComments(data.total_comments);
      }
    } catch (err) {
      setError('Failed to load comments');
    } finally {
      setLoading(false);
    }
  }, [reviewId]);

  useEffect(() => {
    fetchComments();
  }, [fetchComments]);

  const handleNewComment = async (content) => {
    if (!isAuthenticated) { navigate('/login'); return; }
    try {
      const response = await fetch(`${API_URL}/api/comments`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ review_id: reviewId, content }),
      });
      if (response.ok) {
        const data = await response.json();
        setComments([data.comment, ...comments]);
        setTotalComments(totalComments + 1);
      }
    } catch (err) {
      console.error('Failed to post comment:', err);
    }
  };

  const handleReply = async (parentId, content) => {
    if (!isAuthenticated) { navigate('/login'); return; }
    try {
      const response = await fetch(`${API_URL}/api/comments`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ review_id: reviewId, content, parent_id: parentId }),
      });
      if (response.ok) {
        const data = await response.json();
        setComments(comments.map(c => c.id === parentId ? { ...c, replies: [...(c.replies || []), data.comment] } : c));
        setTotalComments(totalComments + 1);
      }
    } catch (err) {
      console.error('Failed to post reply:', err);
    }
  };

  const handleEdit = async (commentId, content) => {
    try {
      const response = await fetch(`${API_URL}/api/comments/${commentId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ content }),
      });
      if (response.ok) {
        const update = (list) => list.map(c => {
          if (c.id === commentId) return { ...c, content, updated_at: new Date().toISOString() };
          if (c.replies) return { ...c, replies: update(c.replies) };
          return c;
        });
        setComments(update(comments));
      }
    } catch (err) {
      console.error('Failed to edit:', err);
    }
  };

  const handleDelete = async (commentId) => {
    try {
      const response = await fetch(`${API_URL}/api/comments/${commentId}`, { method: 'DELETE', credentials: 'include' });
      if (response.ok) {
        const remove = (list) => list.filter(c => {
          if (c.id === commentId) return false;
          if (c.replies) c.replies = remove(c.replies);
          return true;
        });
        setComments(remove(comments));
        setTotalComments(Math.max(0, totalComments - 1));
      }
    } catch (err) {
      console.error('Failed to delete:', err);
    }
  };

  return (
    <div className="p-6 bg-[#121917] border border-[#52ea88]/10">
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-lg font-semibold text-[#E6FFF6] flex items-center gap-2">
          <MessageSquare className="w-5 h-5 text-[#52ea88]" />
          Discussion
          {totalComments > 0 && <span className="text-sm font-normal text-[#9FBFB4]">({totalComments})</span>}
        </h3>
      </div>
      {isAuthenticated ? (
        <div className="mb-6">
          <CommentForm placeholder="Share your thoughts on this film..." onSubmit={handleNewComment} />
        </div>
      ) : (
        <div className="mb-6 p-4 bg-[#0B0F0E] border border-[#52ea88]/10 text-center">
          <p className="text-[#9FBFB4] text-sm mb-3">Sign in to join the discussion</p>
          <button onClick={() => navigate('/login')} className="px-4 py-2 bg-[#52ea88] text-[#0B0F0E] font-medium text-sm hover:bg-[#52ea88]/90 transition-colors">
            Sign In
          </button>
        </div>
      )}
      {loading ? (
        <div className="flex items-center justify-center py-8">
          <div className="w-8 h-8 border-2 border-[#52ea88] border-t-transparent rounded-full animate-spin" />
        </div>
      ) : error ? (
        <div className="text-center py-8">
          <p className="text-red-400 text-sm">{error}</p>
          <button onClick={fetchComments} className="mt-2 text-[#52ea88] text-sm hover:underline">Try again</button>
        </div>
      ) : comments.length === 0 ? (
        <div className="text-center py-8">
          <MessageSquare className="w-12 h-12 text-[#52ea88]/20 mx-auto mb-3" />
          <p className="text-[#9FBFB4]/70 text-sm">No comments yet. Be the first to share your thoughts!</p>
        </div>
      ) : (
        <div className="divide-y divide-[#52ea88]/5">
          {comments.map((comment) => (
            <CommentItem key={comment.id} comment={comment} onReply={handleReply} onEdit={handleEdit} onDelete={handleDelete} />
          ))}
        </div>
      )}
    </div>
  );
};

export default CommentsSection;
